import { useEffect, useState } from 'react';
import { getDashboardStats } from '../api/dashboard-api';
import type { RecentReading } from '../api/dashboard-api';
import type { Alert, SystemHealthDevice, ActivityFeedItem } from '@/types/api';
import { useOrg } from '@/store/org-context';

interface DashboardStats {
  properties: number;
  propertiesThisMonth: number;
  devices: number;
  activeAlerts: number;
  activeDevices: number;
}

export function useDashboard() {
  const { activeOrgId } = useOrg();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    properties: 0,
    propertiesThisMonth: 0,
    devices: 0,
    activeAlerts: 0,
    activeDevices: 0,
  });
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([]);
  const [recentReadings, setRecentReadings] = useState<RecentReading[]>([]);
  const [systemHealthDevices, setSystemHealthDevices] = useState<SystemHealthDevice[]>([]);
  const [lowBatteryCount, setLowBatteryCount] = useState(0);
  const [weakSignalCount, setWeakSignalCount] = useState(0);
  const [connectionIssuesCount, setConnectionIssuesCount] = useState(0);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);

  useEffect(() => {
    async function loadDashboard() {
      try {
        const response = await getDashboardStats();
        const data = response.data;
        if (!data) return;

        setStats({
          properties: data.totalProperties,
          propertiesThisMonth: data.propertiesThisMonth,
          devices: data.totalDevices,
          activeAlerts: data.activeAlerts,
          activeDevices: data.activeDevices,
        });
        setRecentAlerts(data.recentAlerts ?? []);
        setRecentReadings(data.recentReadings ?? []);
        setSystemHealthDevices(data.systemHealthDevices ?? []);
        setLowBatteryCount(data.lowBatteryCount ?? 0);
        setWeakSignalCount(data.weakSignalCount ?? 0);
        setConnectionIssuesCount(data.connectionIssuesCount ?? 0);
        setActivityFeed(data.activityFeed ?? []);
      } catch (error) {
        console.error('Failed to load dashboard:', error);
      } finally {
        setLoading(false);
      }
    }

    loadDashboard();
  }, [activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { loading, stats, recentAlerts, recentReadings, systemHealthDevices, lowBatteryCount, weakSignalCount, connectionIssuesCount, activityFeed };
}
